import { useRouter } from "next/router"
import { type PropsWithChildren } from "react"
import { Container, SkipToContent } from "@octopus-energy/coral/atoms"
import { IconsThemeProvider } from "@octopus-energy/icons"
import { GdprPrivacyPolicy } from "@/components/atoms"
import { GlobalBannerContainer } from "@/features/banner"
import { MasqueradeBadge } from "@/features/masquerade"
import { getPartnerConfig, type Partner, PartnerFooter, usePartnerTheme } from "@/features/partners"
import { coralIconConfig } from "@/lib/coral/theme"
import { getQuerylessPath } from "@/lib/routes"
import { AppHeader, NavlessHeader } from "../Header"
import { AppFooter } from "./Footer"
import { css } from "@octopus-energy/coral-design/css"

const MAIN_CONTENT_ID = "main-content"

type AppLayoutProps = PropsWithChildren<{
    partner?: Partner | null
    isNavless?: boolean
    hasPrivacyPolicy?: boolean
    href?: string | null
    isHeadless?: boolean
}>

export function AppLayout({
    children,
    partner = null,
    isNavless = false,
    href = null,
    isHeadless = false,
}: AppLayoutProps) {
    const config = getPartnerConfig(partner)

    const router = useRouter()
    const isThemeEnabled = config.routes?.includes(getQuerylessPath(router.pathname))
    const logo = isThemeEnabled ? config.logo : (config.altLogo ?? config.logo)

    // Apply Panda CSS partner theme
    const { isThemeReady } = usePartnerTheme(isThemeEnabled ? partner : null)

    const header = isNavless ? (
        <NavlessHeader
            logo={logo}
            href={href ?? undefined}
        />
    ) : (
        <AppHeader />
    )

    const footer = config.footer ? (
        <PartnerFooter
            links={config.footer.links}
            shouldDisplayPrivacyPolicy={config.footer.shouldDisplayPrivacyPolicy}
        />
    ) : (
        <AppFooter isNavless={isNavless} />
    )

    return (
        <IconsThemeProvider defaultProps={coralIconConfig}>
            <div
                className={css({
                    opacity: isThemeReady ? "1" : "0",
                    transition: "opacity 0.1s ease-in",
                })}
            >
                <MasqueradeBadge />
                <SkipToContent id={MAIN_CONTENT_ID} />

                {!isHeadless && header}

                {!isHeadless && <GlobalBannerContainer />}

                <main id={MAIN_CONTENT_ID}>
                    <Container
                        fullHeight
                        theme="base500"
                        maxWidth="full"
                    >
                        {children}
                    </Container>
                </main>

                {!isHeadless && footer}
            </div>
        </IconsThemeProvider>
    )
}

AppLayout.messages = [
    ...AppHeader.messages,
    ...AppFooter.messages,
    ...PartnerFooter.messages,
    ...GdprPrivacyPolicy.messages,
] as const
